import { Icon } from "@iconify/react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  IconButton,
  TextField,
  Typography
} from "@mui/material";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLikedProducts } from "../contexts/LikedProductsContext";

const Search = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [like, setLiked] = useState(false);
  const { likedProducts, addLikedProduct, removeLikedProduct, isProductLiked } =
    useLikedProducts();

  const products =
    location?.state?.products?.products ||
    location?.state?.image_product?.object ||
    location?.state?.products ||
    [];

  const handleLike = (e, product) => {
    e.stopPropagation();
    console.log(isProductLiked(product.UPC));
    if (isProductLiked(product.UPC)) {
      removeLikedProduct(product.UPC);
      setLiked(false);
    } else {
      addLikedProduct(product);
      setLiked(true);
    }
  };

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        backgroundColor: "white",
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingBottom: 4,
      }}
    >
      {/* Header Section */}
      <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center", // Keeps title centered
            width: "100%",
            padding: "16px 32px",
            paddingTop: "60px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 10,
          }}
        >
          {/* Back Button (Left-Aligned) */}
          <IconButton 
            size="medium" 
            sx={{ 
              color: "black", 
              position: "absolute", 
              left: 32 // Ensures back button stays on the left
            }} 
            onClick={() => navigate(-2)}
          >
            <ArrowBackIosNewIcon fontSize="medium" />
          </IconButton>

          {/* Title (Centered) */}
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              color: "#112A3B", 
              textAlign: "center",
            }}
          >
            Search Products
          </Typography>
        </Box>

  
      {/* Search Bar */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "700px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 3,
          position: "sticky",
          top: 60,
          backgroundColor: "white",
          zIndex: 10,
          paddingTop: 1,
        }}
      >
        <TextField
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && search.trim() !== "") {
              navigate(`/products/details/${search}`);
            }
          }}
          variant="outlined"
          placeholder="Search"
          sx={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          }}
          InputProps={{
            endAdornment: (
              <IconButton
                  onClick={() => {
                    if (search.trim() !== "") {
                      navigate(`/products/details/${search}`);
                    }
                  }}
                >
                <SearchIcon sx={{ color: "#EF4F26" }} />
              </IconButton>
            ),
          }}
        />
      </Box>
  
      {/* Scrollable Product List */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "800px", // Increased width for better use of space
          backgroundColor: "white",
          paddingX: 2,
          height: "70vh", // Restrict height to enable scrolling
          overflowY: "auto", // Enables vertical scrolling inside the container
          scrollbarWidth: "thin", // Firefox scrollbar fix
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#bbb",
            borderRadius: "10px",
          },
        }}
      >
        {products.length > 0 ? (
          products.map((product, index) => (
            <Card
              key={index}
              sx={{
                mb: 2,
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                cursor: "pointer",
                transition: "0.2s ease-in-out",
                "&:hover": { boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.15)" },
                padding: "15px",
                height: "130px", // Increased card height to fill more space
              }}
              onClick={() => navigate(`/products/details/${product.UPC}`)}
            >
              {/* Product Image */}
              <CardMedia
                component="img"
                sx={{
                  width: 100, // Increased image size
                  height: 100,
                  borderRadius: 3,
                  objectFit: "contain",
                  marginLeft: 2,
                }}
                image={product.Image || `https://placehold.co/400x400?text=${encodeURIComponent(product.Name)}`}
                alt={product.Name}
              />
  
              {/* Product Details */}
              <Box sx={{ flex: 1, paddingX: 3 }}>
                <CardContent sx={{ padding: "0px" }}>
                  {/* Product Title */}
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      fontSize: "18px", // Increased font size
                      fontWeight: 600,
                    }}
                  >
                    {product.Name}
                  </Typography>
  
                  {/* Ratings & Actions */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      ⭐ 4.9 (302 Ratings)
                    </Typography>
  
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* Chat Icon */}
                      <IconButton sx={{ transform: "scale(1.1)" }} onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/products/chat/${product.UPC}`)}}>
                        <Icon icon="heroicons:chat-bubble-left-ellipsis" style={{ color: "grey", fontSize: 24 }} />
                      </IconButton>
  
                      {/* Store Icon */}
                      <IconButton sx={{ transform: "scale(1.1)" }} onClick={() => navigate(`/products/details/${product.UPC}`)}>
                        <Icon icon={"bx:store"} style={{ color: "grey", fontSize: 24 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Box>
            </Card>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", marginTop: 2 }}>
            No products found.
          </Typography>
        )}
      </Box>
    </Container>
  );
  };

export default Search;
